import { notifyResize } from "#skyward_window";
import { parseDate } from "./skyward.base.time";
import { show } from "./skyward.ui.collapsePanel";
import { getContentMessageArea } from "./skyward.ui.contentMessageArea";
import { getSvg } from "./skyward.ui.svg";
import { Dictionary } from "./types";

export interface ValidationData
{
    errors: Dictionary<string, ValidationMessage[]>;
    warnings: Dictionary<string, ValidationMessage[]>;
    warninghash?: string;
    warninghashes?: any; // todo 6399417: finish typing this
    preSaveWarningHash?: string;
}

export type ValidationMessage = string | { message: string; };

enum Severity
{
    Error = "Error",
    Warning = "Warning"
}

interface ValidationItem
{
    resultControlName: string;
    target: JQuery;
    hasTarget: boolean;
    control: JQuery;
    isVisible: boolean;
    messages: ValidationMessage[];
    isBase: boolean;
}

interface ValidationType
{
    Severity: Severity;
    SkipStyling: (item?: ValidationItem) => boolean;
}

interface ValidationMessageListItem
{
    key: string;
    value:
    {
        target: JQuery;
        validationList: JQuery;
    };
}

export const classNames = Object.freeze(
{
    errorControl: "error-control",
    errorText: "error-text",
    warningText: "warning-text",
    warningControl: "warning-control",
    persist: "persist",
});

const skyward = window.skyward;

const validationTypes: Record<Severity, ValidationType> =
{
    [Severity.Error]:
    {
        Severity: Severity.Error,
        SkipStyling: function () { return false; }
    },
    [Severity.Warning]:
    {
        Severity: Severity.Warning,
        SkipStyling: function (item) { return item ? true : false; }
    }
};

export function getHtmlViewers(selector: JQuery<HTMLElement>)
{
    return selector.find(".htmlViewer, .framedHTMLViewer").not(".js-ignoreIFrameContents");
}

export function clearValidationMessages(container: JQuery<HTMLElement> | string, baseErrorContainer?: JQuery<HTMLElement>)
{
    // TODO does not passing warninghash, etc cause any problems?
    displayValidationMessages({ errors: {}, warnings: {} }, container, baseErrorContainer);
}

export function displayValidationMessages(data: ValidationData | null, container: JQuery<HTMLElement> | string, baseErrorContainer?: JQuery<HTMLElement>, overrideBase = false)
{
    // Workflow.js was designed to pass in a jQuery object, so lets account for both possibilities:
    const jQContainer = (typeof container === "string") ? $(`#${container}`) : container;
    const contentMessageArea = getContentMessageArea(jQContainer.parent());

    //Remove previous error/warning styling
    jQContainer.find(".objectErrors").html("");
    jQContainer.find(classNames.errorControl.dot()).removeClass(classNames.errorControl).removeAttr("aria-invalid");
    jQContainer.find(classNames.errorText.dot()).not(classNames.persist.dot()).removeClass(classNames.errorText);
    jQContainer.find(classNames.warningControl.dot()).removeClass(classNames.warningControl);
    jQContainer.find(classNames.warningText.dot()).not(classNames.persist.dot()).removeClass(classNames.warningText);
    jQContainer.find(".help").empty();

    // Remove htmlViewer errors/warnings
    jQContainer.find(".js-htmlViewerWrapper").each(function ()
    {
        const wrapper = $(this);
        const control = wrapper.children().not(".js-htmlViewerOverlay");

        wrapper.replaceWith(control);
    });

    if (contentMessageArea)
    {
        contentMessageArea.resetValidation();
    }

    const validationMessageListsForTargets: ValidationMessageListItem[] = [];

    // Apply error messages
    if (data !== null && typeof data === "object")
    {
        const baseUl = $("<ul/>");

        const sumVisibleMessages = getMessageAccumulator(false);

        if (typeof data.errors === "string")
        {
            data.errors = { "Base": [data.errors] };
        }

        const { found: foundErrorCollection, notFound: notFoundErrorCollection } = processValidationCollection(data.errors);
        const { found: foundWarningCollection, notFound: notFoundWarningCollection } = processValidationCollection(data.warnings);
        const allErrors = $.extend({}, foundErrorCollection, notFoundErrorCollection);
        const allWarnings = $.extend({}, foundWarningCollection, notFoundWarningCollection);

        const iframes = getHtmlViewers(jQContainer);
        const promises = Promise.all(iframes.toArray().map(function (elem)
        {
            return skyward.iframeListener.displayValidationMessages(elem, {
                errors: mapValues(notFoundErrorCollection, elem => elem!.messages),
                warnings: mapValues(notFoundWarningCollection, elem => elem!.messages),
            });
        }));
        return promises.then(foundMessagesPerIframe =>
        {
            const allFoundMessages = new Set(([] as string[]).concat(
                Object.keys(foundWarningCollection),
                Object.keys(foundErrorCollection),
                ...foundMessagesPerIframe
            ));
            // If this is the top frame, move any errors that weren't found in iframes back to
            // the found collection, so they can be displayed in the base validation container
            if (window === window.parent)
            {
                for (const key of Object.keys(notFoundWarningCollection))
                {
                    if (!allFoundMessages.has(key))
                    {
                        foundWarningCollection[key] = notFoundWarningCollection[key];
                    }
                }
                for (const key of Object.keys(notFoundErrorCollection))
                {
                    if (!allFoundMessages.has(key))
                    {
                        foundErrorCollection[key] = notFoundErrorCollection[key];
                    }
                }
            }

            let errorCount = (Object.values(allErrors) as ValidationItem[]).reduce(sumVisibleMessages, 0);
            displayValidationListItems(foundErrorCollection, foundErrorCollection, baseUl, validationTypes.Error);

            let warningCount = (Object.values(allWarnings) as ValidationItem[]).reduce(sumVisibleMessages, 0);
            displayValidationListItems(foundWarningCollection, foundErrorCollection, baseUl, validationTypes.Warning);

            // Update the warning hash for this container
            jQContainer.data("warninghash", data.warninghash ?? null);
            jQContainer.data("warninghashes", data.warninghashes);
            jQContainer.data("preSaveWarningHash", data.preSaveWarningHash ?? null);

            // Finally, insert all base error/warning messages
            if (baseErrorContainer)
            {
                baseErrorContainer.attr("role", "alert").html(baseUl[0]);

                // Count for base errors/warnings that use control names other than "Base" or "base"
                errorCount += baseErrorContainer.find(classNames.errorText.dot()).length;
                warningCount += baseErrorContainer.find(classNames.warningText.dot()).length;
            }

            // Update and Display Content Message Area
            if (contentMessageArea)
            {
                const sumAllMessages = getMessageAccumulator(true);
                const totalErrors = (Object.values(allErrors) as ValidationItem[]).reduce(sumAllMessages, 0);

                if (totalErrors > 0 && errorCount === 0)
                {
                    // Special Validation Message
                    contentMessageArea.setAllErrorsAreHidden(true);
                }
                contentMessageArea.setValidationCount(errorCount, warningCount).showValidation();
            }
            notifyResize();
            return Array.from(allFoundMessages);
        });
    }
    else
    {
        notifyResize();
        return Promise.resolve([]);
    }

    function processValidationCollection(validationCollection: Dictionary<string, ValidationMessage[]>)
    {
        const found: Dictionary<string, ValidationItem> = {};
        const notFound: Dictionary<string, ValidationItem> = {};
        for (const controlName in validationCollection)
        {
            const processed = processValidationItem(controlName, validationCollection);
            if (processed.hasTarget)
            {
                found[controlName] = processed;
            }
            else
            {
                notFound[controlName] = processed;
            }
        }
        return { found, notFound };
    }

    function processValidationItem(controlName: string, validationCollection: Dictionary<string, ValidationMessage[]>)
    {
        let resultControlName = controlName;
        let resultTarget = getHelpMessage(controlName, jQContainer);
        let resultControl = jQContainer.find(`[name="${resultControlName}"]`);

        if (resultTarget.length <= 0)
        {
            const controlWithoutQualifier = controlName.split(".")[1];
            const targetsWithoutQualifier = getHelpMessage(controlWithoutQualifier, jQContainer);
            if (targetsWithoutQualifier.length > 0)
            {
                resultControlName = controlWithoutQualifier;
                resultTarget = targetsWithoutQualifier;
                resultControl = jQContainer.find(`[name="${controlWithoutQualifier}"]`);
            }
        }
        const isBase = resultControlName.toLowerCase() === "base" || overrideBase;

        // Expand collapse panels if the target is within one, so that validation messages are visible and not just getting the contact district admin message.
        if (resultTarget.is('.collapsePanelContents.collapsed *'))
        {
            resultTarget.parents('.collapsePanelContents.collapsed').each(function ()
            {
                show($(this).closest('.collapsePanel'));
            });
        }

        return {
            resultControlName: resultControlName,
            target: resultTarget,
            hasTarget: resultTarget.length > 0 && !isBase,
            control: resultControl,
            isVisible: resultTarget.is(":visible"),
            messages: validationCollection[controlName]!,
            isBase: isBase,
        };
    }

    function getMessageAccumulator(includeInvisible: boolean)
    {
        return function (accumulator: number, currentValue: ValidationItem): number
        {
            if (includeInvisible || currentValue.isVisible || currentValue.isBase)
            {
                return accumulator + currentValue.messages.length;
            }

            return accumulator;
        };
    }

    function displayValidationListItems(validationCollection: Dictionary<string, ValidationItem>, errorCollection: Dictionary<string, ValidationItem>, baseUl: JQuery, type: ValidationType)
    {
        const baseUiMessages = [];
        const seenBaseMessages = new Set();

        for (const controlName in validationCollection)
        {
            const item = validationCollection[controlName]!;
            const isBase = item.isBase;
            // Reset target to prevent future iterations from appending the wrong validation messages
            let target = null;

            // Add error/warning-control class to collapse panels to assist with finding hidden errors instead of getting contact district admin message.
            // Don't add if panel already has error-control
            if (item.target.length > 0)
            {
                item.target.parents('.collapsePanel:not(.error-control)').addClass(`${type.Severity.toLowerCase()}-control`);
            }

            if (!isBase)
            {
                target = item.target;

                if (target.length > 0 && !item.isVisible)
                {
                    continue;
                }
            }

            const listItems = generateValidationListItems(item.messages, item.resultControlName, jQContainer, type.Severity, type.SkipStyling(errorCollection[controlName]), overrideBase);

            if (isBase)
            {
                baseUl.append(listItems);
                baseUiMessages.push(listItems);
            }
            else
            {
                const stringifiedMessages = item.messages.map(message => (typeof message === "string") ? message : message.message).join("\n");

                if (target !== null && target.length > 0)
                {
                    appendValidationMessageListToTarget(target, listItems, controlName);
                    const dirty = item.control.attr("data-dirty");
                    if (dirty === undefined || dirty === "false")
                    {
                        item.control.attr("data-original-value-invalid", "");
                    }
                }
                else if (!seenBaseMessages.has(stringifiedMessages))
                {
                    baseUl.append(listItems);
                    baseUiMessages.push(listItems);
                    seenBaseMessages.add(stringifiedMessages);
                }
            }
        }
    }

    function appendValidationMessageListToTarget(target: JQuery, validationListItems: HTMLElement[], controlName: string)
    {
        const validationListId = `${controlName}-validationList`;

        const validationList = $("<ul>",
        {
            class: "validationList",
            id: validationListId,
            append: validationListItems
        });

        const targetValidation: ValidationMessageListItem["value"] =
        {
            target: target.append(validationList),
            validationList: validationList,
        };

        validationMessageListsForTargets.push({ key: controlName, value: targetValidation });

        const inputElements = jQContainer.find(`[data-validate-control-for="${controlName}"], .electronicsignature[id="${controlName}"]`);
        for (const inputElement of inputElements)
        {
            const inputs = getInputsForControl($(inputElement));
            if (inputs?.length)
            {
                inputs.attr("aria-describedby", validationListId);
                inputs.attr("aria-invalid", "true");
            }
        }
    }
}

export function setAriaInvalid(element: JQuery<HTMLElement>, isInvalid: boolean)
{
    const inputs = getInputsForControl(element);
    if (inputs?.length)
    {
        if (isInvalid && inputs.is(":input, .radioButtonGroup"))
        {
            inputs.attr("aria-invalid", "true");
        }
        else
        {
            inputs.removeAttr("aria-invalid");
        }
    }
}

export function validDate(date: string | Date)
{
    const parsed = parseDate(date, false);
    return validYear(parsed.getFullYear());
}

// a data-decimal-mask allows a negative sign and a period. A valid number on the UI side cannot contain either without a number present
export function validNumber(n: string | number)
{
    return !isNaN(typeof n === "number" ? n : parseFloat(n)) && isFinite(n as number);
}

export function getControl(name: string, container?: JQuery<HTMLElement>)
{
    const selector = `[data-validate-control-for="${name}"]`;
    return container ? container.find(selector) : $(selector);
}

export function getHelpMessage(name: string, container?: JQuery<HTMLElement>)
{
    const selector = `[data-validate-message-for="${name}"]`;
    return container ? container.find(selector) : $(selector);
}

export function getLabel(name: string, container?: JQuery<HTMLElement>)
{
    const selector = `[data-validate-text-for="${name}"]`;
    return container ? container.find(selector) : $(selector);
}

function mapValues<T extends Record<PropertyKey, any>, TOut>(
    target: T,
    mapFn: <K extends keyof T>(value: T[K], key: K, obj: T) => TOut
): { [K in keyof T]: TOut }
{
    const result = {} as { [K in keyof T]: TOut };

    for (const targetKey in target)
    {
        if (Object.prototype.hasOwnProperty.call(target, targetKey))
        {
            const key = targetKey as keyof T;
            result[key] = mapFn(target[key], key, target);
        }
    }

    return result;
}

function validYear(year: number)
{
    // make sure year is above sql min and below skyward max
    return !isNaN(year) && year >= 1 && year <= 9999;
}

function getInputsForControl(element: JQuery)
{
    if (element.is(":input,.radioButtonGroup"))
    {
        return element;
    }
    else if (element.hasClass("eELInputContainer"))
    {
        return element.find(".eELInputContainer__textBox");
    }
    else if (element.is(".checkboxGroup,.multipleSelectBox,.electronicsignature"))
    {
        return element.find(".checkbox");
    }
    else if (element.hasClass("fileUpload2"))
    {
        return element.find(".fileUpload2__selectButton");
    }
    return undefined;
}

function generateValidationListItems(data: ValidationMessage[], control: string, jQContainer: JQuery, severity: Severity, skipStyling: boolean, overrideBase: boolean)
{
    const listItems = [];
    const cssSeverity = severity.toLowerCase();

    for (const itemContent of data)
    {
        let message = itemContent;
        if (typeof itemContent !== "string")
        {
            message = itemContent.message;
        }

        if (message !== "")
        {
            const svgElement = getSvg(severity, severity)
                .addClass([`alert${severity}`, "svgNoHover"])
                .attr("aria-role", "img");

            const srOnlyLabel = $("<span>",
            {
                class: "sr-only",
                text: severity
            });

            const messageElement = $("<span>",
            {
                addClass: "validationSpan",
                text: message
            });

            const listItem = $("<li>",
            {
                addClass: `${cssSeverity}${(control.toLowerCase() === "base" || overrideBase) ? "-list" : "-text"}`,
                append: $("<span>",
                {
                    append: [svgElement, srOnlyLabel, messageElement]
                })
            });

            listItems.push(listItem[0]);

            if (!skipStyling && control.toLowerCase() !== "base" && !overrideBase)
            {
                getLabel(control, jQContainer).addClass(`${cssSeverity}-text`);
                const controlElements = getControl(control, jQContainer);
                controlElements.addClass(`${cssSeverity}-control`).trigger("errorChange");
                if (severity === Severity.Error)
                {
                    controlElements.each(function ()
                    {
                        const inputs = getInputsForControl($(this));
                        if (inputs !== undefined)
                        {
                            inputs.attr("aria-invalid", "true");
                        }
                    });
                }
                // only need to do this once per control.
                skipStyling = true;
            }

        }
    }

    return listItems;
}
